import { useEffect, useRef, useState, useMemo } from 'react';
import { Listbox, Combobox } from '@headlessui/react';
import {
	CheckIcon,
	ChevronUpDownIcon,
	PlusIcon,
} from '@heroicons/react/20/solid';
import {
	InformationCircleIcon,
	ClockIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { createWorkOrder } from '../api';
import { getTeam } from '../api/company';
import { getAssets } from '../api/assets';
import { getApprovedSops, getSopVersions } from '../api/sops';
import { getModuleCategoriesByModuleKey } from '../api/modules';
import { getContacts } from '../api/contacts';
import { getContracts } from '../api/contracts';

import {
	StackableModal,
	DateTimePicker,
	AssetSelectorBeta,
	VendorSelectorBeta,
} from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'createWorkOrder';

const CreateWorkOrderModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openAddCategory } = useModal('addCategoryModal');
	const titleInputRef = useRef(null);

	const [form, setForm] = useState({
		title: '',
		description: '',
		categoryId: null,
		assignedTo: null,
		scheduledStartDate: '',
		scheduledEndDate: '',
		dueDate: '',
		allDay: true,
		startTime: '09:00',
		endTime: '17:00',
		status: 'draft',
		sopId: null,
		sopVersionId: null, // null = always use latest
		requireSopCompletion: false,
		contractId: null, // Single contract for the work order
		vendorId: null, // Single vendor for the work order
		assets: [], // Array of { assetId, contractId }
		contacts: [], // Array of contact IDs
		serviceReportRequired: false,
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [categories, setCategories] = useState([]);
	const [sopVersions, setSopVersions] = useState([]);
	const [loadingVersions, setLoadingVersions] = useState(false);
	const [sopQuery, setSopQuery] = useState('');
	const sopComboboxButtonRef = useRef(null);

	// Data fetching
	const { value: team } = useAsync(getTeam);
	const { value: assets } = useAsync(getAssets, {
		defaultPayload: { pageSize: null },
	});
	const { value: sops } = useAsync(getApprovedSops, {
		defaultPayload: {},
	});
	const { value: contacts } = useAsync(getContacts, {
		defaultPayload: { pageSize: null },
	});
	const { value: contracts } = useAsync(getContracts, {
		defaultPayload: { status: 'active', pageSize: null },
	});

	// Process contracts with selected asset counts
	const processedContracts = useMemo(() => {
		if (!contracts?.rows) return [];
		const selectedAssetIds = form.assets.map((a) => a.assetId);
		const totalSelected = selectedAssetIds.length;

		return contracts.rows
			.filter((contract) => contract.active)
			.map((contract) => {
				const contractAssetIds = (contract.assets || []).map((a) => a.id);
				const selectedInContract = selectedAssetIds.filter((id) =>
					contractAssetIds.includes(id),
				).length;

				return {
					id: contract.id,
					name: contract.name,
					assets: contract.assets || [],
					selectedInContract,
					totalSelected,
				};
			});
	}, [contracts, form.assets]);

	// Process vendors with selected asset counts
	const processedVendors = useMemo(() => {
		if (!contacts?.rows || !assets?.rows) return [];
		const selectedAssetIds = form.assets.map((a) => a.assetId);
		const totalSelected = selectedAssetIds.length;

		// Get all contacts (vendors can be companies or individuals)
		const vendors = contacts.rows;

		return vendors
			.map((vendor) => {
				// Count how many selected assets have this vendor through their contracts
				let selectedWithVendor = 0;
				selectedAssetIds.forEach((assetId) => {
					const asset = assets.rows.find((a) => a.id === assetId);
					if (asset?.contracts) {
						const hasVendor = asset.contracts.some((contract) =>
							contract.contacts?.some((c) => c.id === vendor.id),
						);
						if (hasVendor) selectedWithVendor++;
					}
				});

				const isCompany = vendor.type === 'company' || !!vendor.companyName;
				const displayName = isCompany
					? vendor.companyName || vendor.name
					: `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim() ||
						vendor.name;

				return {
					id: vendor.id,
					name: displayName,
					isCompany,
					email: vendor.email,
					selectedWithVendor,
					totalSelected,
				};
			})
			.sort((a, b) => {
				// Primary: sort by number of matching selected assets (descending)
				if (b.selectedWithVendor !== a.selectedWithVendor) {
					return b.selectedWithVendor - a.selectedWithVendor;
				}
				// Secondary: sort alphabetically by name (ascending)
				return a.name.localeCompare(b.name);
			});
	}, [contacts, assets, form.assets]);

	// Process assets data for selector
	const processedAssets = useMemo(() => {
		if (!assets?.rows) return [];
		return assets.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assets]);

	// Filter and sort SOPs based on search query
	const filteredSops = useMemo(() => {
		const sopList = sops?.rows || sops || [];
		const sorted = [...sopList].sort((a, b) =>
			(a.name || '').localeCompare(b.name || '', undefined, {
				numeric: true,
				sensitivity: 'base',
			}),
		);
		if (!sopQuery.trim()) return sorted;
		const query = sopQuery.toLowerCase();
		return sorted.filter(
			(sop) =>
				sop.name?.toLowerCase().includes(query) ||
				sop.description?.toLowerCase().includes(query),
		);
	}, [sops, sopQuery]);

	// Selected SOP for display
	const selectedSop = useMemo(() => {
		const sopList = sops?.rows || sops || [];
		return sopList.find((s) => s.id === form.sopId);
	}, [sops, form.sopId]);

	// Fetch categories
	const fetchCategories = async () => {
		try {
			const result = await getModuleCategoriesByModuleKey({
				key: 'default:work-order',
			});
			setCategories(result || []);
		} catch (error) {
			console.error('Failed to fetch categories:', error);
			setCategories([]);
		}
	};

	useEffect(() => {
		if (open) {
			fetchCategories();

			// Pre-fill ticket ID if passed
			if (open?.ticketId) {
				setForm((prev) => ({ ...prev, ticketId: open.ticketId }));
			}

			// Pre-fill asset if passed from ticket
			if (open?.asset) {
				const activeContract = getActiveContract(open.asset);
				setForm((prev) => ({
					...prev,
					assets: [
						{
							assetId: open.asset.id,
							contractId: activeContract?.id || null,
						},
					],
				}));
			}

			// Pre-fill service period date if passed from calendar
			if (open?.initialDate) {
				setForm((prev) => ({
					...prev,
					scheduledStartDate: open.initialDate,
					scheduledEndDate: open.initialDate,
					status: 'scheduled',
				}));
			}

			// Pre-fill contract if passed
			if (open?.contractId) {
				setForm((prev) => ({ ...prev, contractId: open.contractId }));
			}
		}
	}, [open]);

	// Fetch SOP versions when SOP is selected
	useEffect(() => {
		if (form.sopId) {
			setLoadingVersions(true);
			setForm((prev) => ({ ...prev, sopVersionId: null })); // Reset version selection
			getSopVersions({ id: form.sopId })
				.then((versions) => {
					setSopVersions(versions || []);
				})
				.catch((err) => {
					console.error('Failed to load SOP versions:', err);
					setSopVersions([]);
				})
				.finally(() => {
					setLoadingVersions(false);
				});
		} else {
			setSopVersions([]);
			setForm((prev) => ({ ...prev, sopVersionId: null }));
		}
	}, [form.sopId]);

	// Auto-select vendors from selected assets' contracts
	useEffect(() => {
		if (!assets?.rows || !contacts?.rows) return;

		const vendorIds = new Set();

		// Extract vendors from selected assets' contracts
		form.assets.forEach((selectedAsset) => {
			const asset = assets.rows.find((a) => a.id === selectedAsset.assetId);
			if (asset?.contracts) {
				asset.contracts.forEach((contract) => {
					if (contract.contacts) {
						contract.contacts.forEach((contact) => {
							vendorIds.add(contact.id);
						});
					}
				});
			}
		});

		// Update form contacts with unique vendor IDs
		const uniqueVendorIds = Array.from(vendorIds);
		if (
			uniqueVendorIds.length > 0 &&
			JSON.stringify(uniqueVendorIds.sort()) !==
				JSON.stringify([...form.contacts].sort())
		) {
			setForm((prev) => ({ ...prev, contacts: uniqueVendorIds }));
		}
	}, [form.assets, assets?.rows, contacts?.rows]);

	const handleClose = () => {
		setForm({
			title: '',
			description: '',
			categoryId: null,
			assignedTo: null,
			scheduledStartDate: '',
			scheduledEndDate: '',
			dueDate: '',
			allDay: true,
			startTime: '09:00',
			endTime: '17:00',
			status: 'draft',
			sopId: null,
			sopVersionId: null,
			requireSopCompletion: false,
			contractId: null,
			vendorId: null,
			assets: [],
			contacts: [],
			serviceReportRequired: false,
		});
		setSopVersions([]);
		setClose();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title.trim()) return;

		setIsSubmitting(true);

		// Combine dates with times if not all-day
		let scheduledStartDate = form.scheduledStartDate || null;
		let scheduledEndDate = form.scheduledEndDate || null;

		if (!form.allDay && scheduledStartDate) {
			// Extract date part and append time
			const startDateOnly = scheduledStartDate.split('T')[0];
			scheduledStartDate = `${startDateOnly}T${form.startTime}:00`;
		}
		if (!form.allDay && scheduledEndDate) {
			const endDateOnly = scheduledEndDate.split('T')[0];
			scheduledEndDate = `${endDateOnly}T${form.endTime}:00`;
		}

		try {
			const workOrderData = {
				title: form.title,
				description: form.description,
				categoryId: form.categoryId,
				scheduledStartDate,
				scheduledEndDate,
				dueDate: form.dueDate || null,
				allDay: form.allDay,
				status: form.status,
				ticketId: open?.ticketId || null,
				sopId: form.sopId,
				sopVersionId: form.sopId ? form.sopVersionId : null, // null = always use latest
				requireSopCompletion: form.sopId ? form.requireSopCompletion : false,
				contractId: form.contractId || null,
				vendorId: form.vendorId || null,
				serviceReportRequired: form.serviceReportRequired,
				assets: form.assets.map((a) => ({
					assetId: a.assetId,
					contractId: a.contractId || null,
				})),
				contacts: form.contacts,
			};

			// Set assignedTo if provided
			if (form.assignedTo) {
				workOrderData.assignedTo = form.assignedTo;
			}

			await createWorkOrder(workOrderData);

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to create work order:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Get active contract for an asset
	const getActiveContract = (asset) => {
		if (!asset?.contracts?.length) return null;
		return asset.contracts.find((c) => c.active);
	};

	// Determine if we're creating from a ticket (with pre-selected asset)
	const isFromTicket = !!open?.asset;

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title="Create Work Order"
			size={isFromTicket ? 'lg' : '4xl'}
			initialFocusRef={titleInputRef}
		>
			<form onSubmit={handleSubmit}>
				<div className={isFromTicket ? '' : 'grid grid-cols-2 gap-6'}>
					<div className={isFromTicket ? '' : 'col-span-1'}>
						{/* Details */}
						<div className="space-y-4">
							{!open?.asset && (
								<>
									{/* Asset Vendor Info Banner */}
									{open?.asset?.contacts?.length > 0 && (
										<div className="rounded-md bg-blue-50 p-3 border border-blue-200">
											<div className="flex">
												<div className="flex-shrink-0">
													<InformationCircleIcon className="h-5 w-5 text-blue-400" />
												</div>
												<div className="ml-3">
													<p className="text-sm text-blue-700">
														<span className="font-medium">
															{open.asset.manufacturerName ||
																open.asset.manufacturer?.name ||
																'Asset'}{' '}
															{open.asset.modelName ||
																open.asset.model?.name ||
																''}
														</span>
														{' has '}
														{open.asset.contacts.length === 1
															? 'a vendor'
															: `${open.asset.contacts.length} vendors`}
														{' attached: '}
														{open.asset.contacts.map((c, i) => (
															<span key={c.id}>
																{c.companyName ||
																	`${c.firstName || ''} ${c.lastName || ''}`.trim()}
																{i < open.asset.contacts.length - 1 ? ', ' : ''}
															</span>
														))}
													</p>
												</div>
											</div>
										</div>
									)}
								</>
							)}

							{/* Title */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Title <span className="text-red-500">*</span>
								</label>
								<input
									ref={titleInputRef}
									type="text"
									value={form.title}
									onChange={(e) => setForm({ ...form, title: e.target.value })}
									placeholder="Enter work order title"
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
									required
								/>
							</div>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Description
								</label>
								<textarea
									value={form.description}
									onChange={(e) =>
										setForm({ ...form, description: e.target.value })
									}
									placeholder="Enter description..."
									rows={3}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
								/>
							</div>

							{/* Category and Due Date Row */}
							<div className="grid grid-cols-2 gap-4">
								{/* Date Range */}
								<div className="col-span-2">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Service Period
									</label>
									<DateTimePicker
										mode="range"
										startDate={form.scheduledStartDate}
										endDate={form.scheduledEndDate}
										onChange={(startDate, endDate) => {
											setForm({
												...form,
												scheduledStartDate: startDate,
												scheduledEndDate: endDate,
												status: startDate ? 'scheduled' : 'draft',
												allDay: true,
											});
										}}
										placeholder="Select date range"
										showTimeToggle={false}
										defaultAllDay={true}
										allDay={form.allDay}
									/>

									{/* All Day / Timed Toggle */}
									{form?.scheduledStartDate && (
										<>
											<div className="mt-3 flex items-center mb-4">
												<div className="flex items-center gap-2">
													<ClockIcon className="h-4 w-4 text-gray-400" />
													<span className="text-sm text-gray-700">
														All day event
													</span>
												</div>
												<button
													type="button"
													onClick={() => {
														const newAllDay = !form.allDay;
														if (newAllDay) {
															// Reset times to defaults and strip time from dates when switching to all-day
															const startDateOnly = form.scheduledStartDate
																? form.scheduledStartDate.split('T')[0]
																: '';
															const endDateOnly = form.scheduledEndDate
																? form.scheduledEndDate.split('T')[0]
																: '';
															setForm({
																...form,
																allDay: true,
																startTime: '09:00',
																endTime: '17:00',
																scheduledStartDate: startDateOnly,
																scheduledEndDate: endDateOnly,
															});
														} else {
															setForm({ ...form, allDay: false });
														}
													}}
													className={classNames(
														'relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
														!form.allDay ? 'bg-gray-200' : 'bg-indigo-600',
													)}
												>
													<span
														className={classNames(
															'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
															!form.allDay ? 'translate-x-0' : 'translate-x-5',
														)}
													/>
												</button>
											</div>

											{/* Time Pickers (shown when not all-day) */}
											{!form.allDay && (
												<div className="mt-3 grid grid-cols-2 gap-4">
													<div>
														<label className="block text-xs font-medium text-gray-500 mb-1">
															Start Time
														</label>
														<select
															value={form.startTime}
															onChange={(e) => {
																const newStartTime = e.target.value;
																// If end time is same or earlier, set it to 30 mins after start
																const startMinutes =
																	parseInt(newStartTime.split(':')[0]) * 60 +
																	parseInt(newStartTime.split(':')[1]);
																const endMinutes =
																	parseInt(form.endTime.split(':')[0]) * 60 +
																	parseInt(form.endTime.split(':')[1]);

																// Get date portions
																const startDateOnly = form.scheduledStartDate
																	? form.scheduledStartDate.split('T')[0]
																	: '';
																const endDateOnly = form.scheduledEndDate
																	? form.scheduledEndDate.split('T')[0]
																	: '';

																if (endMinutes <= startMinutes) {
																	const newEndMinutes = startMinutes + 30;
																	const endHours =
																		Math.floor(newEndMinutes / 60) % 24;
																	const endMins = newEndMinutes % 60;
																	const newEndTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
																	setForm({
																		...form,
																		startTime: newStartTime,
																		endTime: newEndTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																} else {
																	setForm({
																		...form,
																		startTime: newStartTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																	});
																}
															}}
															className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
														>
															{Array.from({ length: 48 }, (_, i) => {
																const hours = Math.floor(i / 2);
																const minutes = i % 2 === 0 ? '00' : '30';
																const time = `${String(hours).padStart(2, '0')}:${minutes}`;
																const displayTime = `${hours === 0 ? 12 : hours > 12 ? hours - 12 : hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
																return (
																	<option key={time} value={time}>
																		{displayTime}
																	</option>
																);
															})}
														</select>
													</div>
													<div>
														<label className="block text-xs font-medium text-gray-500 mb-1">
															End Time
														</label>
														<select
															value={form.endTime}
															onChange={(e) => {
																const newEndTime = e.target.value;
																// If end time is before start time, set start to 30 mins before end
																const startMinutes =
																	parseInt(form.startTime.split(':')[0]) * 60 +
																	parseInt(form.startTime.split(':')[1]);
																const endMinutes =
																	parseInt(newEndTime.split(':')[0]) * 60 +
																	parseInt(newEndTime.split(':')[1]);

																// Get date portions
																const startDateOnly = form.scheduledStartDate
																	? form.scheduledStartDate.split('T')[0]
																	: '';
																const endDateOnly = form.scheduledEndDate
																	? form.scheduledEndDate.split('T')[0]
																	: '';

																if (endMinutes <= startMinutes) {
																	const newStartMinutes = Math.max(
																		0,
																		endMinutes - 30,
																	);
																	const startHours = Math.floor(
																		newStartMinutes / 60,
																	);
																	const startMins = newStartMinutes % 60;
																	const newStartTime = `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`;
																	setForm({
																		...form,
																		startTime: newStartTime,
																		endTime: newEndTime,
																		scheduledStartDate: startDateOnly
																			? `${startDateOnly}T${newStartTime}:00`
																			: '',
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																} else {
																	setForm({
																		...form,
																		endTime: newEndTime,
																		scheduledEndDate: endDateOnly
																			? `${endDateOnly}T${newEndTime}:00`
																			: '',
																	});
																}
															}}
															className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
														>
															{Array.from({ length: 48 }, (_, i) => {
																const hours = Math.floor(i / 2);
																const minutes = i % 2 === 0 ? '00' : '30';
																const time = `${String(hours).padStart(2, '0')}:${minutes}`;
																const displayTime = `${hours === 0 ? 12 : hours > 12 ? hours - 12 : hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
																return (
																	<option key={time} value={time}>
																		{displayTime}
																	</option>
																);
															})}
														</select>
													</div>
												</div>
											)}
										</>
									)}
								</div>

								{/*<div className="col-span-1">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Due By
									</label>
									<DateTimePicker
										value={form.dueDate}
										startDate={form.scheduledStartDate}
										endDate={form.scheduledEndDate}
										onChange={(dueDate) => {
											setForm({
												...form,
												dueDate,
												status:
													form?.status !== 'draft'
														? form?.status
														: dueDate
															? 'pending'
															: 'draft',
											});
										}}
										placeholder="Due by date"
										showTimeToggle={false}
										defaultAllDay={true}
									/>
								</div>*/}

								{/* Category */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Category
									</label>
									<Listbox
										value={form.categoryId}
										onChange={(val) => setForm({ ...form, categoryId: val })}
									>
										<div className="relative">
											<Listbox.Button
												type="button"
												className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
											>
												<span>
													{form.categoryId
														? categories?.find((c) => c.id === form.categoryId)
																?.name
														: 'Select category'}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												<Listbox.Option
													value={null}
													className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
												>
													No category
												</Listbox.Option>
												{[...(categories || [])]
													.sort((a, b) =>
														(a.name || '').localeCompare(
															b.name || '',
															undefined,
															{
																numeric: true,
																sensitivity: 'base',
															},
														),
													)
													.map((category) => (
														<Listbox.Option
															key={category.id}
															value={category.id}
															className={({ active }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<>
																	<span>{category.name}</span>
																	{selected && (
																		<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																			<CheckIcon className="h-5 w-5" />
																		</span>
																	)}
																</>
															)}
														</Listbox.Option>
													))}
												<div className="border-t border-gray-100 mt-1 pt-1">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															openAddCategory({
																moduleKey: 'default:work-order',
																onSubmit: (category) => {
																	setForm((prev) => ({
																		...prev,
																		categoryId: category?.value,
																	}));
																	fetchCategories();
																},
															});
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-medium"
													>
														<PlusIcon className="h-4 w-4" />
														Add category
													</button>
												</div>
											</Listbox.Options>
										</div>
									</Listbox>
								</div>

								{/* Assign to Team Member */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Assign To
									</label>
									<Listbox
										value={form.assignedTo}
										onChange={(val) => setForm({ ...form, assignedTo: val })}
									>
										<div className="relative">
											<Listbox.Button
												type="button"
												className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
											>
												<span>
													{form.assignedTo
														? getUserFullName(
																team?.rows?.find(
																	(u) => u.id === form.assignedTo,
																),
															)
														: 'Select team member'}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												{team?.rows?.map((user) => (
													<Listbox.Option
														key={user.id}
														value={user.id}
														className={({ active }) =>
															classNames(
																active ? 'bg-indigo-50' : '',
																'relative cursor-pointer select-none py-2 pl-3 pr-9',
															)
														}
													>
														{({ selected }) => (
															<>
																<span>{getUserFullName(user)}</span>
																{selected && (
																	<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																		<CheckIcon className="h-5 w-5" />
																	</span>
																)}
															</>
														)}
													</Listbox.Option>
												))}
											</Listbox.Options>
										</div>
									</Listbox>
								</div>

								{/* Status */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Status
									</label>
									<Listbox
										value={form.status}
										onChange={(val) => setForm({ ...form, status: val })}
									>
										<div className="relative">
											<Listbox.Button
												type="button"
												className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
											>
												<span className="capitalize">
													{form.status.replace(/_/g, ' ')}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												<Listbox.Option
													value="draft"
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<span className="capitalize">Draft</span>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
												<Listbox.Option
													value="scheduled"
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<span className="capitalize">Scheduled</span>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
												<Listbox.Option
													value="in_progress"
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<span className="capitalize">In Progress</span>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											</Listbox.Options>
										</div>
									</Listbox>
								</div>
							</div>

							{/* SOP Template */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									SOP Template
								</label>
								<Combobox
									value={form.sopId}
									onChange={(value) => {
										setForm({ ...form, sopId: value });
										setSopQuery('');
									}}
								>
									<div className="relative">
										<div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
											<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
											<Combobox.Input
												className="w-full border-none py-2 pl-9 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none"
												placeholder={selectedSop?.name || 'Search SOPs...'}
												displayValue={() => (sopQuery ? sopQuery : '')}
												onChange={(e) => setSopQuery(e.target.value)}
												onFocus={(e) => {
													if (selectedSop) {
														e.target.placeholder = selectedSop.name;
													}
													sopComboboxButtonRef.current?.click();
												}}
											/>
											<Combobox.Button
												ref={sopComboboxButtonRef}
												className="absolute inset-y-0 right-0 flex items-center pr-2"
											>
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</Combobox.Button>
										</div>
										<Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											{selectedSop && (
												<Combobox.Option
													value={null}
													className={({ active }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
														)
													}
												>
													<span className="text-gray-500">Clear selection</span>
												</Combobox.Option>
											)}
											{filteredSops.length === 0 && sopQuery !== '' ? (
												<div className="relative cursor-default select-none py-2 px-4 text-gray-500">
													No SOPs found.
												</div>
											) : (
												filteredSops.map((sop) => (
													<Combobox.Option
														key={sop.id}
														value={sop.id}
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div>
																<div className="flex items-center justify-between">
																	<span
																		className={selected ? 'font-semibold' : ''}
																	>
																		{sop.name}
																	</span>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600" />
																	)}
																</div>
																<p className="text-xs text-gray-500 mt-0.5">
																	{sop.versionMajor !== undefined &&
																	sop.versionMinor !== undefined
																		? `Latest: v${sop.versionMajor}.${sop.versionMinor}`
																		: sop.steps?.length > 0
																			? `${sop.steps.length} step${sop.steps.length !== 1 ? 's' : ''}`
																			: 'Unpublished'}
																</p>
															</div>
														)}
													</Combobox.Option>
												))
											)}
										</Combobox.Options>
									</div>
								</Combobox>

								{/* SOP Version Selection - only show when SOP is selected */}
								{form.sopId && (
									<>
										<div className="mt-2">
											<label className="block text-xs text-gray-500 mb-1">
												SOP Version
											</label>
											<Listbox
												value={form.sopVersionId}
												onChange={(val) =>
													setForm({ ...form, sopVersionId: val })
												}
											>
												<div className="relative">
													<Listbox.Button
														type="button"
														className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
													>
														{loadingVersions ? (
															<span className="text-gray-400">
																Loading versions...
															</span>
														) : form.sopVersionId ? (
															<span>
																Version{' '}
																{
																	sopVersions.find(
																		(v) => v.id === form.sopVersionId,
																	)?.versionMajor
																}
																.
																{
																	sopVersions.find(
																		(v) => v.id === form.sopVersionId,
																	)?.versionMinor
																}
															</span>
														) : (
															<span className="text-gray-700">
																Always use latest version
															</span>
														)}
														<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
															<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
														</span>
													</Listbox.Button>
													<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
														<Listbox.Option
															value={null}
															className={({ active, selected }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	selected ? 'bg-indigo-100' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<div className="flex items-center justify-between">
																	<div>
																		<span
																			className={
																				selected ? 'font-semibold' : ''
																			}
																		>
																			Always use latest version
																		</span>
																		<p className="text-xs text-gray-500">
																			Work order will always use the most recent
																			SOP version
																		</p>
																	</div>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600" />
																	)}
																</div>
															)}
														</Listbox.Option>
														{sopVersions.map((version) => (
															<Listbox.Option
																key={version.id}
																value={version.id}
																className={({ active, selected }) =>
																	classNames(
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																		'relative cursor-pointer select-none py-2 pl-3 pr-9',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center justify-between">
																		<div>
																			<span
																				className={
																					selected ? 'font-semibold' : ''
																				}
																			>
																				Version {version.versionMajor}.
																				{version.versionMinor}
																			</span>
																			{version.publishedAt && (
																				<p className="text-xs text-gray-500">
																					Published{' '}
																					{new Date(
																						version.publishedAt,
																					).toLocaleDateString()}
																				</p>
																			)}
																		</div>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
													</Listbox.Options>
												</div>
											</Listbox>
										</div>

										{/* Require SOP Completion checkbox */}
										<div className="flex items-center gap-3 pt-2 mt-3">
											<input
												type="checkbox"
												checked={form.requireSopCompletion}
												onChange={(e) =>
													setForm({
														...form,
														requireSopCompletion: e.target.checked,
													})
												}
												className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
											/>
											<div>
												<label
													htmlFor="serviceReportRequired"
													className="text-sm font-medium text-gray-700"
												>
													SOP Completion Required
												</label>
												<p className="text-xs text-gray-500">
													An SOP must be completed before the work order can be
													completed
												</p>
											</div>
										</div>
									</>
								)}
							</div>

							{/* Service Report Required */}
							<div className="flex items-center gap-3 pt-2">
								<input
									type="checkbox"
									id="serviceReportRequired"
									checked={form.serviceReportRequired}
									onChange={(e) =>
										setForm({
											...form,
											serviceReportRequired: e.target.checked,
										})
									}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<div>
									<label
										htmlFor="serviceReportRequired"
										className="text-sm font-medium text-gray-700"
									>
										Service Report Required
									</label>
									<p className="text-xs text-gray-500">
										A service report must be uploaded before the work order can
										be completed
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Right Column: Assets, Contract, Vendor (only when not from ticket) */}
					{!isFromTicket && (
						<div className="col-span-1">
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Assets
									</label>
									<p className="text-xs text-gray-500 mb-3">
										Select assets for this work order
									</p>

									{/* Asset Selector */}
									<AssetSelectorBeta
										value={form.assets.map((a) => a.assetId)}
										onChange={(assetIds) => {
											setForm((prev) => ({
												...prev,
												assets: assetIds.map((assetId) => {
													// Preserve existing contractId if asset was already selected
													const existing = prev.assets.find(
														(a) => a.assetId === assetId,
													);
													return existing || { assetId, contractId: null };
												}),
											}));
										}}
										assets={processedAssets}
										placeholder="Select assets..."
										multiple={true}
										allowGroups={false}
									/>
								</div>
								<div>
									{/* Contract Selection */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Contract
										</label>
										<Listbox
											value={form.contractId}
											onChange={(val) => setForm({ ...form, contractId: val })}
										>
											<div className="relative">
												<Listbox.Button
													type="button"
													className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
												>
													<span>
														{form.contractId
															? processedContracts?.find(
																	(c) => c.id === form.contractId,
																)?.name || 'Selected contract'
															: 'Select contract (optional)'}
													</span>
													<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
														<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
													</span>
												</Listbox.Button>
												<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
													<Listbox.Option
														value={null}
														className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
													>
														No contract
													</Listbox.Option>
													{processedContracts?.map((contract) => (
														<Listbox.Option
															key={contract.id}
															value={contract.id}
															className={({ active }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<>
																	<div>
																		<span className="font-medium">
																			{contract.name}
																		</span>
																		{contract.totalSelected > 0 && (
																			<p className="text-xs text-gray-500 mt-0.5">
																				{contract.selectedInContract} out of{' '}
																				{contract.totalSelected} selected asset
																				{contract.totalSelected !== 1
																					? 's'
																					: ''}{' '}
																				in contract
																			</p>
																		)}
																	</div>
																	{selected && (
																		<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																			<CheckIcon className="h-5 w-5" />
																		</span>
																	)}
																</>
															)}
														</Listbox.Option>
													))}
												</Listbox.Options>
											</div>
										</Listbox>
									</div>
								</div>

								{/* Vendor Selection */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Vendors
									</label>
									<VendorSelectorBeta
										value={form.contacts}
										onChange={(vendorIds) =>
											setForm({ ...form, contacts: vendorIds })
										}
										vendors={processedVendors}
										placeholder="Select vendors..."
										multiple={true}
										grouped={true}
									/>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Actions */}
				<div className="flex justify-end gap-3 pt-6 mt-6">
					<button
						type="button"
						onClick={handleClose}
						className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!form.title.trim() || isSubmitting}
						className={classNames(
							'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
							!form.title.trim() || isSubmitting
								? 'bg-gray-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700',
						)}
					>
						{isSubmitting ? 'Creating...' : 'Create Work Order'}
					</button>
				</div>
			</form>
		</StackableModal>
	);
};

export default CreateWorkOrderModal;
